import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/layout/PageHeader";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/contexts/ToastContext";
import {
  catalogApi,
  AutofillResponse,
  CreateCatalogRequest,
  ItemCopyDetails,
} from "@/lib/api/catalog";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { useItemTypes } from "@/hooks/useItemTypes";
import { useLocations } from "@/hooks/useLocations";
import { useCollections } from "@/hooks/useCollections";
import { useBookConditions } from "@/hooks/useBookConditions";
import { useItemStatuses } from "@/hooks/useItemStatuses";
import { useCatalogSettings } from "@/hooks/useCatalogSettings";
import { useTenant } from "@/contexts/TenantContext";
import { isValidIsbn as isIsbnFormatValid } from "@/lib/isbn";
import { formatNumberWithCommas } from "@/lib/formatters";
import { CopyDetailsEditor, ItemCopyData } from "@/components/catalog/CopyDetailsEditor";
import { CatalogExistingBiblioSummary } from "@/components/catalog/CatalogExistingBiblioSummary";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { isFieldRequiredOnPublish } from "@/lib/catalog/catalog-required-fields";
import { validateCatalogForm } from "@/lib/catalog/catalog-validate-form";
import { CatalogWorkflowButtons } from "@/components/catalog/CatalogWorkflowButtons";
import {
  executeCatalogWorkflowAction,
  type CatalogWorkflowAction,
} from "@/lib/catalog/catalog-workflow";
import {
  Scan,
  BookOpen,
  Search,
  Check,
  AlertCircle,
  Sparkles,
  Plus,
  Minus,
  BookMarked,
  Package,
  MapPin,
  Layers,
  Tag,
  Printer,
  ExternalLink,
  Barcode,
  Clock,
  CheckCircle2,
  XCircle,
  X,
  Info,
  ClipboardCheck,
  Building,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { openIsbnSpineLabelPrint } from "@/lib/isbnLabelPrint";

type CatalogStep = "scan" | "preview" | "success";

const ISBN_FORM_ERR_RING =
  "border-destructive ring-2 ring-destructive/20 border-destructive focus-visible:ring-destructive";

const ISBN_FORM_FIELD_KEYS = [
  "isbn",
  "publisher",
  "placeOfPublication",
  "publishedDate",
  "callNumber",
  "bindingType",
  "itemTypeId",
  "locationId",
  "branchId",
  "conditionCode",
  "statusCode",
] as const;
type IsbnFormFieldKey = (typeof ISBN_FORM_FIELD_KEYS)[number];

interface RecentEntry {
  id: string;
  title: string;
  authors: string[];
  isbn: string;
  copies: number;
  barcodes: string[];
  timestamp: Date;
}

export function IsbnCatalogPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenant } = useTenant();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  // Step state
  const [currentStep, setCurrentStep] = useState<CatalogStep>("scan");

  // ISBN input state
  const [isbn, setIsbn] = useState("");
  const [isValidIsbn, setIsValidIsbn] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preview data
  const [previewData, setPreviewData] = useState<AutofillResponse | null>(null);
  const [biblio, setBiblio] = useState<AutofillResponse["biblio"] | null>(null);
  const [itemDefaults, setItemDefaults] = useState<any>({});
  const [copies, setCopies] = useState(1);

  // Catalog settings and per-copy data
  const [copyData, setCopyData] = useState<ItemCopyData[]>([{}]);

  // Options (cached via React Query)
  const { data: itemTypesRaw = [], isLoading: loadingTypes } = useItemTypes(true);
  const { data: locationsRaw = [], isLoading: loadingLocs } = useLocations(true);
  const { data: collectionsRaw = [], isLoading: loadingCols } = useCollections(true);
  const { data: conditionsRaw = [], isLoading: loadingConds } = useBookConditions(true);
  const { data: itemStatuses = [], isLoading: loadingStatuses } = useItemStatuses();
  const {
    data: catalogSettings = { accessionNumberMode: "AUTO", approvalWorkflow: "disabled" },
    isLoading: loadingSettings,
  } = useCatalogSettings();
  const { data: branches = [] } = useActiveBranches();

  const itemTypes = itemTypesRaw.filter((t) => t.isActive);
  const locations = locationsRaw.filter((l) => l.isActive);
  const collections = collectionsRaw.filter((c) => c.isActive);
  const conditions = conditionsRaw.filter((c) => c.isActive).sort((a, b) => a.order - b.order);

  const loadingOptions =
    loadingTypes ||
    loadingLocs ||
    loadingCols ||
    loadingConds ||
    loadingStatuses ||
    loadingSettings;

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [createdBarcodes, setCreatedBarcodes] = useState<string[]>([]);

  // Recent entries
  const [recentEntries, setRecentEntries] = useState<RecentEntry[]>([]);

  // Continuous mode
  const [continuousMode, setContinuousMode] = useState(true);

  // Cancel confirmation
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const [isbnFormErrors, setIsbnFormErrors] = useState<Partial<Record<IsbnFormFieldKey, boolean>>>(
    {}
  );
  const [copyAcqErr, setCopyAcqErr] = useState<boolean[]>([]);
  const [copyAccErr, setCopyAccErr] = useState<boolean[]>([]);

  // Editor input (for manual add)
  const [editorInput, setEditorInput] = useState("");
  const isAddCopyFlow = Boolean(previewData?.existingBiblioId);

  // Focus input on step change
  useEffect(() => {
    if (currentStep === "scan" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentStep]);

  // Validate ISBN as user types (9–13 digits; local publisher numbers accepted)
  useEffect(() => {
    if (!isbn.trim()) {
      setIsValidIsbn(null);
      return;
    }
    const valid = isIsbnFormatValid(isbn);
    setIsValidIsbn(valid);
  }, [isbn]);

  const handleIsbnSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isbn.trim() || !isValidIsbn) return;

    setLoading(true);
    setError(null);

    try {
      const data = await catalogApi.autofillMetadata(isbn.trim().replace(/[-\s]/g, ""));
      applyAutofillData(data);
    } catch (err: any) {
      console.error("Autofill error:", err);
      const status = err.response?.status;
      const serverMessage = err.response?.data?.message;
      if (status === 504 || err.code === "ECONNABORTED") {
        setError("Lookup timed out. Please try again in a moment.");
      } else if (status === 404) {
        setError(
          serverMessage || "Could not find book information. Please check the ISBN and try again."
        );
      } else {
        setError(serverMessage || "Something went wrong while looking up this ISBN.");
      }
    } finally {
      setLoading(false);
    }
  };

  const mapAutofillError = (err: any): string => {
    const status = err.response?.status;
    const serverMessage = err.response?.data?.message;
    if (status === 504 || err.code === "ECONNABORTED") {
      return "Lookup timed out. Please try again in a moment.";
    }
    if (status === 404) {
      return (
        serverMessage || "Could not find book information. Please check the ISBN and try again."
      );
    }
    return serverMessage || "Something went wrong while looking up this ISBN.";
  };

  const applyAutofillData = (data: AutofillResponse) => {
    setPreviewData(data);
    setBiblio({ ...data.biblio, editors: data.biblio.editors || [] });
    setItemDefaults(data.suggestedItem || { statusCode: "AVAILABLE" });
    setCopies(1);
    setCopyData(
      data.existingBiblioId
        ? [
            {
              statusCode: data.suggestedItem?.statusCode || "AVAILABLE",
              locationId: data.suggestedItem?.locationId,
              conditionCode: data.suggestedItem?.conditionCode,
              acquisitionMode: data.suggestedItem?.acquisitionMode,
              branchId: data.suggestedItem?.branchId,
            },
          ]
        : [
            {
              statusCode: data.suggestedItem?.statusCode || "AVAILABLE",
              locationId: data.suggestedItem?.locationId,
            },
          ]
    );
    setCurrentStep("preview");
  };

  const handleRefreshMetadata = async () => {
    if (!isbn.trim() || !isValidIsbn) return;
    setLoading(true);
    setError(null);
    try {
      const data = await catalogApi.autofillMetadata(isbn.trim().replace(/[-\s]/g, ""), {
        refresh: true,
      });
      applyAutofillData(data);
    } catch (err: any) {
      console.error("Autofill refresh error:", err);
      setError(mapAutofillError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValidIsbn) {
      handleIsbnSubmit();
    }
  };

  const buildCreatePayload = (saveAsDraft: boolean): CreateCatalogRequest => {
    const itemCopies: ItemCopyDetails[] = copyData.map((copy) => ({
      accessionNumber: copy.accessionNumber,
      conditionCode: copy.conditionCode,
      statusCode: copy.statusCode,
      acquisitionMode: copy.acquisitionMode,
      locationId: copy.locationId,
      branchId: copy.branchId,
    }));

    return {
      biblio: {
        title: biblio!.title,
        subtitle: biblio!.subtitle,
        authors: biblio!.authors,
        editors: biblio!.editors,
        edition: biblio!.edition,
        isbn: biblio!.isbn,
        doi: biblio!.doi,
        publisher: biblio!.publisher,
        placeOfPublication: biblio!.placeOfPublication,
        publishedDate: biblio!.publishedDate,
        pages: biblio!.pages,
        illustrations: biblio!.illustrations,
        size: biblio!.size,
        seriesInformation: biblio!.seriesInformation,
        subjects: biblio!.subjects,
        description: biblio!.description,
        coverUrl: biblio!.coverUrl,
        lcClassification: biblio!.lcClassification,
        callNumber: biblio!.callNumber,
        bindingType: biblio!.bindingType,
      },
      copies,
      itemDefaults: {
        itemTypeId: itemDefaults.itemTypeId || undefined,
        locationId: itemDefaults.locationId || undefined,
        collectionId: itemDefaults.collectionId || undefined,
        branchId: itemDefaults.branchId || undefined,
        conditionCode: itemDefaults.conditionCode || undefined,
        statusCode: itemDefaults.statusCode || "AVAILABLE",
      },
      itemCopies,
      catalogMethod: "ISBN",
      saveAsDraft,
    };
  };

  const clearIsbnFormError = (k: IsbnFormFieldKey) => {
    setIsbnFormErrors((s) => {
      if (!s[k]) return s;
      const n = { ...s };
      delete n[k];
      return n;
    });
  };

  const onCopyDataChange = (d: ItemCopyData[]) => {
    setCopyData(d);
    if (copyAcqErr.length) setCopyAcqErr([]);
    if (copyAccErr.length) setCopyAccErr([]);
  };

  const applyIsbnPublishFieldErrors = (): boolean => {
    const next: Partial<Record<IsbnFormFieldKey, boolean>> = {};
    if (!biblio!.isbn?.trim()) next.isbn = true;
    if (!biblio!.publisher?.trim()) next.publisher = true;
    if (!biblio!.placeOfPublication?.trim()) next.placeOfPublication = true;
    if (!biblio!.publishedDate?.trim()) next.publishedDate = true;
    if (!biblio!.callNumber?.trim()) next.callNumber = true;
    if (!biblio!.bindingType?.trim()) next.bindingType = true;
    if (!itemDefaults.itemTypeId?.trim()) next.itemTypeId = true;
    if (!itemDefaults.locationId?.trim()) next.locationId = true;
    if (branches.length > 0 && !itemDefaults.branchId?.trim()) next.branchId = true;
    if (!itemDefaults.conditionCode?.trim()) next.conditionCode = true;
    if (!itemDefaults.statusCode?.trim()) next.statusCode = true;

    const acqFlags = Array.from({ length: copies }, (_, i) => {
      const a = copyData[i]?.acquisitionMode?.trim();
      return !a;
    });
    const accFlags =
      catalogSettings.accessionNumberMode === "MANUAL"
        ? Array.from(
            { length: copies },
            (_, i) => !copyData[i]?.accessionNumber || (copyData[i]!.accessionNumber ?? 0) <= 0
          )
        : Array.from({ length: copies }, () => false);

    setIsbnFormErrors(next);
    setCopyAcqErr(acqFlags);
    setCopyAccErr(accFlags);

    const hasBiblioFieldErrors = Object.values(next).some(Boolean);
    const hasCopyErr = acqFlags.some(Boolean) || accFlags.some(Boolean);
    if (hasBiblioFieldErrors || hasCopyErr) {
      const firstBiblio = ISBN_FORM_FIELD_KEYS.find((k) => next[k]);
      requestAnimationFrame(() => {
        if (firstBiblio) {
          document
            .getElementById(`isbn-field-${firstBiblio}`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          document
            .getElementById("isbn-field-copy-section")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
      return true;
    }

    return false;
  };

  const applyAddCopyFieldErrors = (): boolean => {
    const next: Partial<Record<IsbnFormFieldKey, boolean>> = {};
    if (!itemDefaults.itemTypeId?.trim()) next.itemTypeId = true;
    if (!itemDefaults.locationId?.trim()) next.locationId = true;
    if (branches.length > 0 && !itemDefaults.branchId?.trim()) next.branchId = true;
    if (!itemDefaults.conditionCode?.trim()) next.conditionCode = true;
    if (!itemDefaults.statusCode?.trim()) next.statusCode = true;

    const acqFlags = Array.from({ length: copies }, (_, i) => {
      const acquisitionMode = copyData[i]?.acquisitionMode?.trim();
      return !acquisitionMode;
    });
    const accFlags =
      catalogSettings.accessionNumberMode === "MANUAL"
        ? Array.from(
            { length: copies },
            (_, i) => !copyData[i]?.accessionNumber || (copyData[i]!.accessionNumber ?? 0) <= 0
          )
        : Array.from({ length: copies }, () => false);

    setIsbnFormErrors(next);
    setCopyAcqErr(acqFlags);
    setCopyAccErr(accFlags);

    const hasDefaultFieldErrors = Object.values(next).some(Boolean);
    const hasCopyErr = acqFlags.some(Boolean) || accFlags.some(Boolean);
    if (hasDefaultFieldErrors || hasCopyErr) {
      const firstField = ISBN_FORM_FIELD_KEYS.find((k) => next[k]);
      requestAnimationFrame(() => {
        if (firstField) {
          document
            .getElementById(`isbn-field-${firstField}`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          document
            .getElementById("isbn-field-copy-section")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
      return true;
    }

    return false;
  };

  const handleWorkflowAction = async (action: CatalogWorkflowAction) => {
    if (!biblio) return;

    if (isAddCopyFlow) {
      if (action !== "publish") {
        toast.error("Existing records only support adding copies.");
        return;
      }

      if (applyAddCopyFieldErrors()) {
        return;
      }

      setIsbnFormErrors({});
      setCopyAcqErr([]);
      setCopyAccErr([]);
      setSubmitting(true);

      try {
        const itemCopies: ItemCopyDetails[] = copyData.map((copy) => ({
          accessionNumber: copy.accessionNumber,
          conditionCode: copy.conditionCode,
          statusCode: copy.statusCode,
          acquisitionMode: copy.acquisitionMode,
          locationId: copy.locationId,
          branchId: copy.branchId,
        }));
        const result = await catalogApi.addCopiesToBiblio(previewData!.existingBiblioId!, {
          copies,
          itemDefaults: {
            itemTypeId: itemDefaults.itemTypeId || undefined,
            locationId: itemDefaults.locationId || undefined,
            collectionId: itemDefaults.collectionId || undefined,
            branchId: itemDefaults.branchId || undefined,
            conditionCode: itemDefaults.conditionCode || undefined,
            statusCode: itemDefaults.statusCode || "AVAILABLE",
          },
          itemCopies,
        });
        const barcodes = result.items.map((item) => item.barcode);

        setRecentEntries((prev) => [
          {
            id: previewData!.existingBiblioId!,
            title: biblio.title,
            authors: biblio.authors,
            isbn: biblio.isbn || "",
            copies,
            barcodes,
            timestamp: new Date(),
          },
          ...prev.slice(0, 9),
        ]);

        setCreatedBarcodes(barcodes);
        setCurrentStep("success");
        if (tenant?.id) {
          await queryClient.invalidateQueries(["catalog", "unified", tenant.id]);
        }
        toast.success(`${copies} new cop${copies === 1 ? "y" : "ies"} added to "${biblio.title}"`);
      } catch (err: any) {
        console.error("Add copies error:", err);
        toast.error(err.response?.data?.message || "Failed to add copies.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const validationErrors = validateCatalogForm(
      {
        biblio,
        copies,
        itemDefaults,
        itemCopies: copyData,
      },
      {
        method: "ISBN",
        mode: action === "draft" ? "draft" : "publish",
        accessionNumberMode: catalogSettings.accessionNumberMode,
        requireBranch: branches.length > 0,
      }
    );

    if (action === "draft") {
      if (Object.keys(validationErrors).length > 0) {
        toast.error(`Please fix required fields: ${Object.values(validationErrors).join("; ")}`);
        return;
      }
    } else {
      const hasFieldErrors = applyIsbnPublishFieldErrors();
      if (hasFieldErrors || Object.keys(validationErrors).length > 0) {
        if (Object.keys(validationErrors).length > 0) {
          toast.error(`Please fix required fields: ${Object.values(validationErrors).join("; ")}`);
        }
        return;
      }
    }

    setIsbnFormErrors({});
    setCopyAcqErr([]);
    setCopyAccErr([]);
    setSubmitting(true);

    try {
      const itemCopies: ItemCopyDetails[] = copyData.map((copy) => ({
        accessionNumber: copy.accessionNumber,
        conditionCode: copy.conditionCode,
        statusCode: copy.statusCode,
        acquisitionMode: copy.acquisitionMode,
        locationId: copy.locationId,
        branchId: copy.branchId,
      }));

      if (action !== "draft" && previewData?.existingBiblioId) {
        const result = await catalogApi.addCopiesToBiblio(previewData.existingBiblioId, {
          copies,
          itemDefaults: {
            itemTypeId: itemDefaults.itemTypeId || undefined,
            locationId: itemDefaults.locationId || undefined,
            collectionId: itemDefaults.collectionId || undefined,
            branchId: itemDefaults.branchId || undefined,
            conditionCode: itemDefaults.conditionCode || undefined,
            statusCode: itemDefaults.statusCode || "AVAILABLE",
          },
          itemCopies,
        });
        const barcodes = result.items.map((i) => i.barcode);

        setRecentEntries((prev) => [
          {
            id: previewData.existingBiblioId!,
            title: biblio.title,
            authors: biblio.authors,
            isbn: biblio.isbn || "",
            copies,
            barcodes,
            timestamp: new Date(),
          },
          ...prev.slice(0, 9),
        ]);

        setCreatedBarcodes(barcodes);
        setCurrentStep("success");
        if (tenant?.id) {
          await queryClient.invalidateQueries(["catalog", "unified", tenant.id]);
        }
        toast.success(`${copies} new cop${copies === 1 ? "y" : "ies"} added to "${biblio.title}"`);
        return;
      }

      const result = await executeCatalogWorkflowAction({
        action,
        createPayload: buildCreatePayload(action === "draft"),
      });

      if (action === "draft") {
        toast.success(`"${biblio.title}" saved as draft.`);
        navigate(`/catalog/physical/${result.biblioId}/edit`);
        return;
      }

      if (action === "submit-for-review") {
        toast.success(`"${biblio.title}" submitted for review.`);
        navigate("/staff/catalog/drafts");
        return;
      }

      const barcodes = (result.items || []).map((item) => item.barcode);

      setRecentEntries((prev) => [
        {
          id: result.biblioId,
          title: biblio.title,
          authors: biblio.authors,
          isbn: biblio.isbn || "",
          copies,
          barcodes,
          timestamp: new Date(),
        },
        ...prev.slice(0, 9),
      ]);

      setCreatedBarcodes(barcodes);
      setCurrentStep("success");
      if (tenant?.id) {
        await queryClient.invalidateQueries(["catalog", "unified", tenant.id]);
      }
      toast.success(`"${biblio.title}" added to catalog!`);
    } catch (err: any) {
      console.error("Create error:", err);
      toast.error(err.response?.data?.message || "Failed to create catalog entry.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    setIsbn("");
    setPreviewData(null);
    setBiblio(null);
    setItemDefaults({});
    setCreatedBarcodes([]);
    setError(null);
    setEditorInput("");
    setCopies(1);
    setCopyData([{}]);
    setIsbnFormErrors({});
    setCopyAcqErr([]);
    setCopyAccErr([]);
    setCurrentStep("scan");
  };

  const handleCancel = () => {
    if (currentStep === "preview" && biblio) {
      setShowCancelDialog(true);
    } else {
      handleAddAnother();
    }
  };

  const confirmCancel = () => {
    handleAddAnother();
    setShowCancelDialog(false);
  };

  const incrementCopies = () => setCopies((c) => Math.min(c + 1, 100));
  const decrementCopies = () => setCopies((c) => Math.max(c - 1, 1));

  const addEditor = () => {
    if (editorInput.trim() && biblio) {
      setBiblio({
        ...biblio,
        editors: [...(biblio.editors || []), editorInput.trim()],
      });
      setEditorInput("");
    }
  };

  const removeEditor = (index: number) => {
    if (biblio) {
      setBiblio({
        ...biblio,
        editors: (biblio.editors || []).filter((_, i) => i !== index),
      });
    }
  };

  // Step indicator
  const steps = [
    { id: "scan", label: "Scan ISBN", icon: Scan },
    { id: "preview", label: "Review & Edit", icon: BookOpen },
    { id: "success", label: "Complete", icon: Check },
  ];

  const getCurrentStepIndex = () => steps.findIndex((s) => s.id === currentStep);

  if (loadingOptions) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl">
          <PageHeader
            title="ISBN Auto-Cataloging"
            description="Add books to your catalog by scanning or entering their ISBN"
            breadcrumbItems={[{ label: "Catalog" }, { label: "ISBN Auto-Catalog" }]}
            showSearch={false}
          />
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner size="xl" label="Loading cataloging tools..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title="ISBN Auto-Cataloging"
          description="Add books to your catalog by scanning or entering their ISBN"
          breadcrumbItems={[{ label: "Catalog" }, { label: "ISBN Auto-Catalog" }]}
          showSearch={false}
        />

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isComplete = getCurrentStepIndex() > index;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center transition-all",
                        isComplete && "bg-green-500 text-white",
                        isActive && "bg-primary text-white shadow-lg scale-110",
                        !isComplete && !isActive && "bg-gray-200 text-gray-500"
                      )}
                    >
                      {isComplete ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <span
                      className={cn(
                        "mt-2 text-sm font-medium",
                        isActive ? "text-primary" : "text-gray-500"
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "w-24 h-1 mx-2 rounded transition-colors",
                        isComplete ? "bg-green-500" : "bg-gray-200"
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* STEP 1: Scan ISBN */}
            {currentStep === "scan" && (
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-8">
                  <div className="max-w-xl mx-auto text-center">
                    {/* Scanner Visual */}
                    <div className="relative mb-8">
                      <div className="flex items-center justify-center w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary/5">
                        <div className="flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-inner">
                          <Barcode className="w-12 h-12 text-primary" />
                        </div>
                      </div>
                      <div className="absolute inset-x-0 flex items-center justify-center top-16">
                        <div className="w-40 h-0.5 bg-red-500 animate-pulse rounded" />
                      </div>
                    </div>

                    <h2 className="mb-2 text-2xl font-bold text-gray-900">Scan or Enter ISBN</h2>
                    <p className="mb-8 text-gray-500">
                      Use a barcode scanner or type the ISBN manually to fetch book details
                      automatically
                    </p>

                    {/* ISBN Input */}
                    <div className="relative max-w-md mx-auto">
                      <div className="relative">
                        <Search className="absolute w-5 h-5 text-gray-400 -translate-y-1/2 left-4 top-1/2" />
                        <Input
                          ref={inputRef}
                          type="text"
                          placeholder="Enter ISBN (e.g., 978-0-13-468599-1)"
                          value={isbn}
                          onChange={(e) => setIsbn(e.target.value)}
                          onKeyDown={handleKeyDown}
                          disabled={loading}
                          className={cn(
                            "h-14 pl-12 pr-12 text-lg text-center font-mono tracking-wider border-2 transition-colors",
                            isValidIsbn === true && "border-green-500 focus:border-green-500",
                            isValidIsbn === false && "border-red-500 focus:border-red-500",
                            isValidIsbn === null && "border-gray-200"
                          )}
                        />
                        {isValidIsbn !== null && (
                          <div className="absolute -translate-y-1/2 right-4 top-1/2">
                            {isValidIsbn ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                        )}
                      </div>

                      {isValidIsbn === false && isbn.trim() && (
                        <p className="mt-2 text-sm text-red-600">
                          Please enter an ISBN with 9–13 digits (dashes optional)
                        </p>
                      )}
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-4 mt-6 text-red-800 border border-red-200 rounded-lg bg-red-50">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <p className="text-sm">{error}</p>
                      </div>
                    )}

                    <Button
                      onClick={() => handleIsbnSubmit()}
                      disabled={loading || !isValidIsbn}
                      size="lg"
                      className="gap-2 mt-8"
                    >
                      {loading ? (
                        <>
                          <span className="w-5 h-5 border-2 border-current rounded-full animate-spin border-t-transparent" />
                          Fetching Book Info...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          Fetch Book Details
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Tips */}
                <div className="p-4 border-t border-gray-100 bg-gray-50">
                  <div className="flex items-start max-w-xl gap-3 mx-auto">
                    <Info className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Pro Tips</p>
                      <ul className="mt-1 space-y-1 text-xs text-gray-500">
                        <li>• Position cursor in the input field before scanning</li>
                        <li>• ISBN can be found on the back cover or copyright page</li>
                        <li>• Both ISBN-10 and ISBN-13 formats are supported</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* STEP 2: Preview & Edit */}
            {currentStep === "preview" && biblio && (
              <div className="space-y-6">
                {isAddCopyFlow ? (
                  <CatalogExistingBiblioSummary
                    biblio={{
                      title: biblio.title,
                      authors: biblio.authors,
                      isbn: biblio.isbn,
                      callNumber: biblio.callNumber,
                      lcClassification: biblio.lcClassification,
                      coverUrl: biblio.coverUrl,
                    }}
                    existingCopyCount={previewData?.existingCopyCount ?? 0}
                    biblioId={previewData!.existingBiblioId!}
                    matchLabel="ISBN"
                  />
                ) : (
                  <>
                    {/* Book Preview Card */}
                    <Card className="overflow-hidden bg-white shadow-sm">
                      <div className="p-6">
                        <div className="flex gap-6">
                          {/* Cover Image */}
                          <div className="shrink-0">
                            {biblio.coverUrl ? (
                              <img
                                src={biblio.coverUrl}
                                alt={biblio.title}
                                className="object-cover w-32 rounded-lg shadow-md h-44"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-32 rounded-lg h-44 bg-gradient-to-br from-gray-100 to-gray-200">
                                <BookMarked className="w-12 h-12 text-gray-400" />
                              </div>
                            )}
                          </div>

                          {/* Book Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h2 className="text-xl font-bold text-gray-900 line-clamp-2">
                                  {biblio.title}
                                </h2>
                                <p className="mt-1 text-gray-600">{biblio.authors.join(", ")}</p>
                              </div>
                              {previewData?.confidence !== undefined && (
                                <div
                                  className={cn(
                                    "px-2.5 py-1 rounded-full text-xs font-medium shrink-0",
                                    previewData.confidence >= 0.8 && "bg-green-100 text-green-700",
                                    previewData.confidence >= 0.5 &&
                                      previewData.confidence < 0.8 &&
                                      "bg-yellow-100 text-yellow-700",
                                    previewData.confidence < 0.5 && "bg-red-100 text-red-700"
                                  )}
                                >
                                  {previewData.confidence >= 0.8 && "High Match"}
                                  {previewData.confidence >= 0.5 &&
                                    previewData.confidence < 0.8 &&
                                    "Medium Match"}
                                  {previewData.confidence < 0.5 && "Low Match"}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-3 mt-4 text-sm text-gray-500">
                              {biblio.publisher && <span>Publisher: {biblio.publisher}</span>}
                              {biblio.publishedDate && <span>• {biblio.publishedDate}</span>}
                              {biblio.pages && (
                                <span>• {formatNumberWithCommas(biblio.pages)} pages</span>
                              )}
                            </div>

                            {biblio.isbn && (
                              <div className="mt-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded font-mono text-sm">
                                  <Barcode className="w-4 h-4" />
                                  {biblio.isbn}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>

                    {/* Editable Fields */}
                    <Card className="overflow-hidden bg-white shadow-sm">
                      <div className="p-6">
                        <h3 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                          <BookOpen className="w-5 h-5 text-primary" />
                          Book Details
                        </h3>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="md:col-span-2">
                            <RequiredLabel
                              htmlFor="title"
                              required={isFieldRequiredOnPublish("ISBN", "title")}
                            >
                              Title
                            </RequiredLabel>
                            <Input
                              id="title"
                              value={biblio.title}
                              onChange={(e) => setBiblio({ ...biblio, title: e.target.value })}
                              className="mt-1.5"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <Label htmlFor="subtitle">Subtitle (optional)</Label>
                            <Input
                              id="subtitle"
                              value={biblio.subtitle || ""}
                              onChange={(e) => setBiblio({ ...biblio, subtitle: e.target.value })}
                              placeholder="e.g., A Modern Approach"
                              className="mt-1.5"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Will be displayed as "Title: Subtitle" on catalog cards
                            </p>
                          </div>

                          <div className="md:col-span-2">
                            <RequiredLabel
                              htmlFor="authors"
                              required={isFieldRequiredOnPublish("ISBN", "authors")}
                            >
                              Authors (comma-separated)
                            </RequiredLabel>
                            <Input
                              id="authors"
                              value={biblio.authors.join(", ")}
                              onChange={(e) =>
                                setBiblio({
                                  ...biblio,
                                  authors: e.target.value
                                    .split(",")
                                    .map((a) => a.trim())
                                    .filter(Boolean),
                                })
                              }
                              className="mt-1.5"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <Label htmlFor="editors">Editors (optional)</Label>
                            <p className="mb-1 text-xs text-gray-500">
                              For textbooks and edited volumes. First = Editor-in-Chief.
                            </p>
                            <div className="flex gap-2">
                              <Input
                                id="editors"
                                placeholder="Enter editor name and press Enter"
                                value={editorInput}
                                onChange={(e) => setEditorInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    addEditor();
                                  }
                                }}
                                className="flex-1"
                              />
                              <Button type="button" variant="outline" onClick={addEditor}>
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            {biblio.editors && biblio.editors.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {biblio.editors.map((editor, index) => (
                                  <span
                                    key={index}
                                    className={cn(
                                      "inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm",
                                      index === 0
                                        ? "bg-primary text-white"
                                        : "bg-primary/10 text-primary"
                                    )}
                                  >
                                    {index === 0 && <span className="text-xs">(E-i-C)</span>}
                                    {editor}
                                    <button
                                      type="button"
                                      onClick={() => removeEditor(index)}
                                      className="ml-1 hover:text-red-500"
                                      aria-label="Remove editor"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="isbn-field-publisher">
                              Publisher <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="isbn-field-publisher"
                              value={biblio.publisher || ""}
                              onChange={(e) => {
                                setBiblio({ ...biblio, publisher: e.target.value });
                                clearIsbnFormError("publisher");
                              }}
                              className={cn(
                                "mt-1.5",
                                isbnFormErrors.publisher && ISBN_FORM_ERR_RING
                              )}
                            />
                          </div>

                          <div>
                            <Label htmlFor="isbn-field-placeOfPublication">
                              Place of Publication <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="isbn-field-placeOfPublication"
                              value={biblio.placeOfPublication || ""}
                              onChange={(e) => {
                                setBiblio({ ...biblio, placeOfPublication: e.target.value });
                                clearIsbnFormError("placeOfPublication");
                              }}
                              placeholder="e.g., Lagos, New York"
                              className={cn(
                                "mt-1.5",
                                isbnFormErrors.placeOfPublication && ISBN_FORM_ERR_RING
                              )}
                            />
                          </div>

                          <div>
                            <Label htmlFor="isbn-field-publishedDate">
                              Publication Date <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="isbn-field-publishedDate"
                              value={biblio.publishedDate || ""}
                              onChange={(e) => {
                                setBiblio({ ...biblio, publishedDate: e.target.value });
                                clearIsbnFormError("publishedDate");
                              }}
                              className={cn(
                                "mt-1.5",
                                isbnFormErrors.publishedDate && ISBN_FORM_ERR_RING
                              )}
                            />
                          </div>

                          <div>
                            <Label htmlFor="edition">Edition (optional)</Label>
                            <Input
                              id="edition"
                              value={biblio.edition || ""}
                              onChange={(e) => setBiblio({ ...biblio, edition: e.target.value })}
                              placeholder="e.g., 2nd ed, 3rd ed, revised"
                              className="mt-1.5"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Leave empty for 1st edition. Enter "2nd ed", "3rd ed", "revised", etc.
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="seriesInformation">Series Information (optional)</Label>
                            <Input
                              id="seriesInformation"
                              value={biblio.seriesInformation || ""}
                              onChange={(e) =>
                                setBiblio({ ...biblio, seriesInformation: e.target.value })
                              }
                              placeholder="e.g., Oxford Studies in Philosophy, Vol. 5"
                              className="mt-1.5"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Include series title and volume information
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="isbn-field-isbn">
                              ISBN <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="isbn-field-isbn"
                              value={biblio.isbn || ""}
                              onChange={(e) => {
                                setBiblio({ ...biblio, isbn: e.target.value });
                                clearIsbnFormError("isbn");
                              }}
                              placeholder="ISBN-10 or ISBN-13"
                              className={cn("mt-1.5", isbnFormErrors.isbn && ISBN_FORM_ERR_RING)}
                            />
                          </div>

                          <div>
                            <Label htmlFor="doi">DOI (optional)</Label>
                            <Input
                              id="doi"
                              value={biblio.doi || ""}
                              onChange={(e) => setBiblio({ ...biblio, doi: e.target.value })}
                              placeholder="Digital Object Identifier"
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label htmlFor="pages">Pages</Label>
                            <Input
                              id="pages"
                              type="number"
                              value={biblio.pages || ""}
                              onChange={(e) =>
                                setBiblio({
                                  ...biblio,
                                  pages: parseInt(e.target.value) || undefined,
                                })
                              }
                              className="mt-1.5"
                            />
                          </div>

                          <div>
                            <Label htmlFor="illustrations">Illustrations (optional)</Label>
                            <Input
                              id="illustrations"
                              value={biblio.illustrations || ""}
                              onChange={(e) =>
                                setBiblio({ ...biblio, illustrations: e.target.value })
                              }
                              placeholder="e.g., ILL, col. ill., maps"
                              className="mt-1.5"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Physical description - type of illustrations
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="size">Size (optional)</Label>
                            <Input
                              id="size"
                              value={biblio.size || ""}
                              onChange={(e) => setBiblio({ ...biblio, size: e.target.value })}
                              placeholder="e.g., iv, 24 cm"
                              className="mt-1.5"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Physical description - size/dimensions
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="isbn-field-callNumber">
                              Call Number <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="isbn-field-callNumber"
                              value={biblio.callNumber || ""}
                              onChange={(e) => {
                                setBiblio({ ...biblio, callNumber: e.target.value.toUpperCase() });
                                clearIsbnFormError("callNumber");
                              }}
                              placeholder="e.g., DT515.83.P2 or DT515.4.F2 2001"
                              className={cn(
                                "mt-1.5 font-mono uppercase",
                                isbnFormErrors.callNumber && ISBN_FORM_ERR_RING
                              )}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Automatically converted to UPPERCASE
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="bindingType">
                              Binding Type / Publication Format{" "}
                              <span className="text-red-500">*</span>
                            </Label>
                            <Select
                              value={biblio.bindingType || ""}
                              onValueChange={(value) => {
                                setBiblio({
                                  ...biblio,
                                  bindingType: value || undefined,
                                });
                                clearIsbnFormError("bindingType");
                              }}
                            >
                              <SelectTrigger
                                id="isbn-field-bindingType"
                                className={cn(
                                  "mt-1.5",
                                  isbnFormErrors.bindingType && ISBN_FORM_ERR_RING
                                )}
                              >
                                <SelectValue placeholder="Select binding" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="hbk">Hardback (HBK)</SelectItem>
                                <SelectItem value="pbk">Paperback (PBK)</SelectItem>
                                <SelectItem value="audiobook">Audiobook</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="md:col-span-2">
                            <RequiredLabel
                              htmlFor="subjects"
                              required={isFieldRequiredOnPublish("ISBN", "subjects")}
                            >
                              Subjects (comma-separated)
                            </RequiredLabel>
                            <Input
                              id="subjects"
                              value={biblio.subjects.join(", ")}
                              onChange={(e) =>
                                setBiblio({
                                  ...biblio,
                                  subjects: e.target.value
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                                })
                              }
                              className="mt-1.5"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                              id="description"
                              value={biblio.description || ""}
                              onChange={(e) =>
                                setBiblio({ ...biblio, description: e.target.value })
                              }
                              rows={3}
                              className="mt-1.5"
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  </>
                )}

                {/* Item Settings */}
                <Card className="overflow-hidden bg-white shadow-sm">
                  <div className="p-6">
                    <h3 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                      <Package className="w-5 h-5 text-teal-600" />
                      Copy Settings
                    </h3>

                    {/* Accession Mode Info */}
                    {catalogSettings.accessionNumberMode === "MANUAL" && (
                      <div className="flex items-start gap-2 p-3 mb-4 border rounded-lg bg-amber-50 border-amber-200">
                        <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-900">
                            Manual Accession Mode
                          </p>
                          <p className="text-xs text-amber-700">
                            Your library is configured to require manual entry of accession numbers.
                            Each copy must have an accession number assigned.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2 lg:grid-cols-3">
                      {/* Number of Copies */}
                      <div>
                        <Label>Number of Copies</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={decrementCopies}
                            disabled={copies <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={copies}
                            onChange={(e) =>
                              setCopies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))
                            }
                            className="w-20 text-center"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={incrementCopies}
                            disabled={copies >= 100}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Item Type */}
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <Tag className="w-4 h-4 text-gray-400" />
                          Item Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={itemDefaults.itemTypeId || ""}
                          onValueChange={(value) => {
                            setItemDefaults({
                              ...itemDefaults,
                              itemTypeId: value || undefined,
                            });
                            clearIsbnFormError("itemTypeId");
                          }}
                        >
                          <SelectTrigger
                            id="isbn-field-itemTypeId"
                            className={cn(
                              "mt-1.5",
                              isbnFormErrors.itemTypeId && ISBN_FORM_ERR_RING
                            )}
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {itemTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Location */}
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          Default Location <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={itemDefaults.locationId || ""}
                          onValueChange={(value) => {
                            setItemDefaults({
                              ...itemDefaults,
                              locationId: value || undefined,
                            });
                            clearIsbnFormError("locationId");
                          }}
                        >
                          <SelectTrigger
                            id="isbn-field-locationId"
                            className={cn(
                              "mt-1.5",
                              isbnFormErrors.locationId && ISBN_FORM_ERR_RING
                            )}
                          >
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map((loc) => (
                              <SelectItem key={loc.id} value={loc.id}>
                                {loc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Condition */}
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <ClipboardCheck className="w-4 h-4 text-gray-400" />
                          Default Condition <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={itemDefaults.conditionCode || ""}
                          onValueChange={(value) => {
                            setItemDefaults({
                              ...itemDefaults,
                              conditionCode: value || undefined,
                            });
                            clearIsbnFormError("conditionCode");
                          }}
                        >
                          <SelectTrigger
                            id="isbn-field-conditionCode"
                            className={cn(
                              "mt-1.5",
                              isbnFormErrors.conditionCode && ISBN_FORM_ERR_RING
                            )}
                          >
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            {conditions.map((cond) => (
                              <SelectItem key={cond.code} value={cond.code}>
                                {cond.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Status */}
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-gray-400" />
                          Default Status <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={itemDefaults.statusCode || ""}
                          onValueChange={(value) => {
                            setItemDefaults({
                              ...itemDefaults,
                              statusCode: value || "AVAILABLE",
                            });
                            clearIsbnFormError("statusCode");
                          }}
                        >
                          <SelectTrigger
                            id="isbn-field-statusCode"
                            className={cn(
                              "mt-1.5",
                              isbnFormErrors.statusCode && ISBN_FORM_ERR_RING
                            )}
                          >
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {itemStatuses.map((status) => (
                              <SelectItem key={status.code} value={status.code}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Collection */}
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <Layers className="w-4 h-4 text-gray-400" />
                          Default Collection
                        </Label>
                        <Select
                          value={itemDefaults.collectionId || "none"}
                          onValueChange={(value) =>
                            setItemDefaults({
                              ...itemDefaults,
                              collectionId: value === "none" ? undefined : value,
                            })
                          }
                        >
                          <SelectTrigger className="mt-1.5">
                            <SelectValue placeholder="Select collection" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {collections.map((col) => (
                              <SelectItem key={col.id} value={col.id}>
                                {col.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Branch */}
                      {branches.length > 0 && (
                        <div>
                          <Label className="flex items-center gap-1.5">
                            <Building className="w-4 h-4 text-gray-400" />
                            Default Branch <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={itemDefaults.branchId || ""}
                            onValueChange={(value) => {
                              setItemDefaults({
                                ...itemDefaults,
                                branchId: value || undefined,
                              });
                              clearIsbnFormError("branchId");
                            }}
                          >
                            <SelectTrigger
                              id="isbn-field-branchId"
                              className={cn(
                                "mt-1.5",
                                isbnFormErrors.branchId && ISBN_FORM_ERR_RING
                              )}
                            >
                              <SelectValue placeholder="Select branch" />
                            </SelectTrigger>
                            <SelectContent>
                              {branches.map((branch) => (
                                <SelectItem key={branch.id} value={branch.id}>
                                  {branch.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Per-Copy Details Editor */}
                    <div className="pt-4 border-t border-gray-200" id="isbn-field-copy-section">
                      <h4 className="mb-3 text-sm font-medium text-gray-900">
                        Copy Details
                        <span className="ml-2 text-xs font-normal text-red-500">
                          * Acquisition mode required for each copy
                        </span>
                        {catalogSettings.accessionNumberMode === "MANUAL" && (
                          <span className="ml-2 text-xs font-normal text-red-500">
                            * Accession numbers required
                          </span>
                        )}
                      </h4>
                      <CopyDetailsEditor
                        copies={copies}
                        copyData={copyData}
                        onCopyDataChange={onCopyDataChange}
                        conditions={conditions}
                        itemStatuses={itemStatuses}
                        locations={locations}
                        branches={branches}
                        isManualMode={catalogSettings.accessionNumberMode === "MANUAL"}
                        defaultCondition={itemDefaults.conditionCode}
                        defaultStatus={itemDefaults.statusCode || "AVAILABLE"}
                        defaultLocationId={itemDefaults.locationId}
                        defaultBranchId={itemDefaults.branchId}
                        highlightCopyAcquisition={copyAcqErr}
                        highlightCopyAccession={copyAccErr}
                      />
                    </div>

                    {/* Barcode Preview */}
                    {previewData?.barcodesPreview && previewData.barcodesPreview.length > 0 && (
                      <div className="p-4 mt-4 border border-gray-200 rounded-lg bg-gray-50">
                        <Label className="text-sm font-medium text-gray-700">
                          Barcodes that will be generated:
                        </Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {previewData.barcodesPreview.slice(0, copies).map((barcode, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 font-mono text-sm bg-white border border-gray-200 rounded"
                            >
                              {barcode}
                            </span>
                          ))}
                          {copies > previewData.barcodesPreview.length && (
                            <span className="px-2 py-1 text-sm text-gray-500">
                              +{copies - previewData.barcodesPreview.length} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Actions */}
                <div className="flex items-center justify-between gap-4">
                  <Button variant="outline" onClick={handleCancel} disabled={submitting}>
                    Cancel
                  </Button>
                  <div className="flex items-center gap-2">
                    {previewData?.canRefresh && previewData.metadataSource !== "tenant" && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRefreshMetadata}
                        disabled={loading || submitting}
                      >
                        Refresh from external sources
                      </Button>
                    )}
                    {isAddCopyFlow ? (
                      <Button
                        type="button"
                        onClick={() => handleWorkflowAction("publish")}
                        disabled={submitting}
                      >
                        Add copy
                      </Button>
                    ) : (
                      <CatalogWorkflowButtons
                        catalogSettings={catalogSettings}
                        loading={submitting}
                        createLabel={`Add to Catalog${copies > 1 ? ` (${formatNumberWithCommas(copies)} copies)` : ""}`}
                        layout="inline"
                        onAction={handleWorkflowAction}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Success */}
            {currentStep === "success" && (
              <Card className="overflow-hidden bg-white shadow-sm">
                <div className="p-8 text-center">
                  <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>

                  <h2 className="mb-2 text-2xl font-bold text-gray-900">Successfully Added!</h2>
                  <p className="mb-6 text-gray-500">
                    {formatNumberWithCommas(createdBarcodes.length)} item
                    {createdBarcodes.length !== 1 ? "s" : ""} added to your catalog
                  </p>

                  {/* Created Barcodes */}
                  <div className="max-w-md p-4 mx-auto mb-6 border border-gray-200 rounded-lg bg-gray-50">
                    <Label className="text-sm font-medium text-gray-700">Created Barcodes:</Label>
                    <div className="flex flex-wrap justify-center gap-2 mt-2">
                      {createdBarcodes.map((barcode, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 font-mono text-sm bg-white border border-gray-200 rounded-md"
                        >
                          {barcode}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col max-w-sm gap-3 mx-auto">
                    <Button onClick={handleAddAnother} size="lg" className="gap-2">
                      <Plus className="w-5 h-5" />
                      Add Another Book
                    </Button>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (
                            !openIsbnSpineLabelPrint({
                              title: biblio?.title || "Item",
                              callNumber: biblio?.callNumber || "",
                              barcodes: createdBarcodes,
                            })
                          ) {
                            toast.error("Allow pop-ups to print labels.");
                          }
                        }}
                        className="flex-1 gap-2"
                      >
                        <Printer className="w-4 h-4" />
                        Print Labels
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/catalog")}
                        className="flex-1 gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Catalog
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar - Recent Entries */}
          <div className="lg:col-span-1">
            <Card className="sticky bg-white shadow-sm top-4">
              <div className="p-4 border-b border-gray-100">
                <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Recently Added
                </h3>
              </div>
              <div className="p-4">
                {recentEntries.length === 0 ? (
                  <div className="py-8 text-center">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">Books you catalog will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentEntries.slice(0, 5).map((entry) => (
                      <div
                        key={entry.id}
                        className="p-3 transition-colors rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                        onClick={() => navigate(`/catalog/physical/${entry.id}`)}
                      >
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {entry.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {entry.authors.slice(0, 2).join(", ")}
                          {entry.authors.length > 2 && ` +${entry.authors.length - 2}`}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span>
                            {formatNumberWithCommas(entry.copies)} cop
                            {entry.copies !== 1 ? "ies" : "y"}
                          </span>
                          <span>•</span>
                          <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Continuous Mode Toggle */}
              <div className="p-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="continuousMode"
                    checked={continuousMode}
                    onCheckedChange={(checked) => setContinuousMode(checked === true)}
                  />
                  <div>
                    <Label htmlFor="continuousMode" className="font-medium cursor-pointer">
                      Continuous Mode
                    </Label>
                    <p className="text-xs text-gray-500">Stay on this page after adding books</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick Stats */}
            {recentEntries.length > 0 && (
              <Card className="p-4 mt-4 bg-gradient-to-br from-primary/10 to-primary/5">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    {formatNumberWithCommas(recentEntries.reduce((acc, e) => acc + e.copies, 0))}
                  </p>
                  <p className="text-sm text-gray-600">Items added this session</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title="Discard changes?"
        description="You have unsaved book information. Are you sure you want to start over?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={confirmCancel}
        variant="warning"
      />
    </div>
  );
}
